import {useValueLists} from '../http/value-lists';
import {Button} from '@ui/buttons/button';
import {KeyboardArrowDownIcon} from '@ui/icons/material/KeyboardArrowDown';
import {Menu, MenuItem, MenuTrigger} from '@ui/menu/menu-trigger';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {useChangeLocale} from '@common/locale-switcher/change-locale';
import {useSettings} from '@ui/settings/use-settings';

export function LocaleSwitcher() {
  const {locale} = useSelectedLocale();
  const changeLocale = useChangeLocale();
  const {data} = useValueLists(['localizations']);
  const {i18n} = useSettings();

  if (!data?.localizations || !locale || !i18n.enable) return null;

  const currentCountryCode = getLocaleCountryCode(locale.language, locale.name);

  return (
    <MenuTrigger
      floatingWidth="auto"
      selectionMode="single"
      selectedValue={locale.language}
      onSelectionChange={value => {
        const newLocale = value as string;
        if (newLocale !== locale?.language) {
          changeLocale.mutate({locale: newLocale});
        }
      }}
    >
      <Button
        disabled={changeLocale.isPending}
        variant="text"
        size="sm"
        className="min-w-0 px-8"
        endIcon={<KeyboardArrowDownIcon />}
      >
        <FlagImage countryCode={currentCountryCode} label={locale.name} />
      </Button>

      <Menu>
        {data.localizations.map(localization => {
          const countryCode = getLocaleCountryCode(
            localization.language,
            localization.name,
          );

          return (
            <MenuItem
              value={localization.language}
              key={localization.language}
              className="capitalize"
            >
              <span className="mr-8">
                <FlagImage countryCode={countryCode} label={localization.name} />
              </span>
              {localization.name}
            </MenuItem>
          );
        })}
      </Menu>
    </MenuTrigger>
  );
}

interface FlagImageProps {
  countryCode: string;
  label?: string;
}

function FlagImage({countryCode, label}: FlagImageProps) {
  return (
    <img
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      srcSet={`https://flagcdn.com/w40/${countryCode}.png 1x, https://flagcdn.com/w80/${countryCode}.png 2x`}
      alt={label || countryCode.toUpperCase()}
      className="h-16 w-24 rounded-sm object-cover shadow-sm"
      loading="lazy"
    />
  );
}

function getLocaleCountryCode(language: string, name?: string): string {
  const normalized = language.toLowerCase();
  const normalizedName = name?.toLowerCase() || '';
  const baseCode = normalized.split('-')[0];
  const countryCode = normalized.split('-')[1];

  if (
    baseCode === 'el' ||
    baseCode === 'gr' ||
    normalizedName.includes('greek') ||
    normalizedName.includes('ελλην')
  ) {
    return 'gr';
  }

  if (
    baseCode === 'en' ||
    normalizedName.includes('english') ||
    normalizedName.includes('αγγλ')
  ) {
    return 'gb';
  }

  const languageToCountry: Record<string, string> = {
    fr: 'fr',
    de: 'de',
    it: 'it',
    es: 'es',
    pt: 'pt',
    nl: 'nl',
    tr: 'tr',
    ru: 'ru',
    uk: 'ua',
    pl: 'pl',
    ro: 'ro',
    bg: 'bg',
    sq: 'al',
    sr: 'rs',
    hr: 'hr',
    mk: 'mk',
    zh: 'cn',
    ja: 'jp',
    ko: 'kr',
    ar: 'sa',
    hi: 'in',
  };

  if (countryCode && /^[a-z]{2}$/i.test(countryCode)) {
    return countryCode;
  }

  return languageToCountry[baseCode] || baseCode;
}